/**
 * BespokeBridge – Communications Management Module API
 * Covers: Email Campaigns, Push Notification Campaigns, WhatsApp Campaigns, Campaign Listing
 *
 * Firestore paths (all under organizations/{orgId}/):
 *   emailCampaigns/{id}        pushCampaigns/{id}       whatsappCampaigns/{id}
 *   emailTemplates/{id}        pushTemplates/{id}        whatsappTemplates/{id}
 *
 * Delivery architecture:
 *   This API writes campaigns to Firestore. Actual delivery is handled by:
 *     Email   → Firebase Extension (Trigger Email) reading from emailCampaigns
 *     Push    → Cloud Function reading pushCampaigns and calling FCM
 *     WhatsApp→ Cloud Function reading whatsappCampaigns and calling Meta Business API
 *   The UI sets status = 'scheduled' | 'sent' to trigger these functions.
 *
 * Resolved design decisions (from spec Q&A):
 *   - Email provider: SendGrid (via Firebase Extension)
 *   - WhatsApp: Meta Business API (registered in BespokeBeauty; needs BespokeBridge config)
 *   - WhatsApp opt-in: auto-opted-in on member/volunteer registration
 *   - Schedule: exact time, no business-hours restriction
 *   - No data migration from BespokeBeauty
 */
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp, Timestamp, writeBatch, setDoc, arrayUnion
} from 'firebase/firestore';
import { db, auth } from '../utils/firebase';

// ── Helpers ────────────────────────────────────────────────────────────────
const orgCol = (orgId, col)     => collection(db, 'organizations', orgId, col);
const orgDoc = (orgId, col, id) => doc(db, 'organizations', orgId, col, id);
const toArr  = snap             => snap.docs.map(d => ({ id: d.id, ...d.data() }));
const now    = ()               => serverTimestamp();

export const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
};
export const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-SG', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS & PRE-BUILT TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

export const CAMPAIGN_STATUSES = [
  { value: 'draft',     label: 'Draft',     color: '#6B7280' },
  { value: 'scheduled', label: 'Scheduled', color: '#F59E0B' },
  { value: 'sending',   label: 'Sending',   color: '#3B82F6' },
  { value: 'sent',      label: 'Sent',      color: '#10B981' },
  { value: 'failed',    label: 'Failed',    color: '#DC2626' },
  { value: 'cancelled', label: 'Cancelled', color: '#EF4444' },
];

export const AUDIENCE_TYPES = [
  { value: 'members',    label: 'Members' },
  { value: 'volunteers', label: 'Volunteers' },
  { value: 'both',       label: 'Members + Volunteers' },
  { value: 'donors',     label: 'Donors' },
  { value: 'all',        label: 'Members + Volunteers + Donors' },
];

export const MEMBER_STATUS_FILTERS = [
  { value: 'active',   label: 'Active' },
  { value: 'at_risk',  label: 'At-Risk' },
  { value: 'alumni',   label: 'Alumni' },
  { value: 'inactive', label: 'Inactive' },
];

// Donors can only be emailed, so Push / WhatsApp screens use these types without donor options.
export const getTargetingTypes = (channel = 'email') => channel === 'email'
  ? TARGETING_TYPES
  : TARGETING_TYPES
      .filter(t => t.value !== 'all_donors')
      .map(t => t.value === 'all_contacts' ? { ...t, label: 'All Contacts (Members + Volunteers)' } : t);

export const TARGETING_TYPES = [
  { value: 'all_contacts',         label: 'All Contacts (Members + Volunteers + Donors)' },
  { value: 'all_members',          label: 'All Members' },
  { value: 'all_volunteers',       label: 'All Volunteers' },
  { value: 'all_donors',           label: 'All Donors' },
  { value: 'member_status',        label: 'By Member Status' },
  { value: 'member_district',      label: 'By Member District' },
  { value: 'member_birthday',      label: 'By Member Birthday Month' },
  { value: 'volunteer_status',     label: 'By Volunteer Status' },
  { value: 'volunteer_district',   label: 'By Volunteer District' },
  { value: 'specific_contacts',    label: 'Specific Contacts' },
];

/** Fetch distinct values of a field from a root collection filtered by orgId */
export const fetchDistinctFieldValues = async (collectionName, orgId, fieldName) => {
  try {
    const snap = await getDocs(query(
      collection(db, collectionName),
      where('orgId', '==', orgId)
    ));
    const values = new Set();
    snap.docs.forEach(d => {
      const v = d.data()[fieldName];
      if (v && typeof v === 'string' && v.trim()) values.add(v.trim());
    });
    return [...values].sort();
  } catch (err) {
    console.error(`fetchDistinctFieldValues(${collectionName}, ${fieldName}):`, err);
    return [];
  }
};

export const BIRTH_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Merge fields supported in email body / push body / WA template variables
export const MERGE_FIELDS = [
  { tag: '{{firstName}}',      label: 'First Name' },
  { tag: '{{fullName}}',       label: 'Full Name' },
  { tag: '{{orgName}}',        label: 'Organisation Name' },
  { tag: '{{eventName}}',      label: 'Event Name' },
  { tag: '{{eventDate}}',      label: 'Event Date' },
  { tag: '{{month}}',          label: 'Month' },
  { tag: '{{programName}}',    label: 'Programme Name' },
  { tag: '{{shiftDate}}',      label: 'Shift Date' },
  { tag: '{{hoursLogged}}',    label: 'Hours Logged' },
  { tag: '{{milestoneLabel}}', label: 'Milestone Label' },
  // Event-change ("Changes to registered event") triggered campaign only
  { tag: '{{eventLocation}}',   label: 'Event Location (updated)' },
  { tag: '{{changeSummary}}',   label: 'Summary of Event Changes' },
  { tag: '{{keepLink}}',        label: 'Keep Registration Link' },
  { tag: '{{cancelLink}}',      label: 'Cancel Registration Link' },
  { tag: '{{manageLink}}',      label: 'Manage Registration Link' },
];

// Trigger context of the event-change campaign. The backend (routes/eventRegistration.js) finds
// the org's template by this exact value, so don't rename it without updating the backend.
export const EVENT_CHANGE_TRIGGER_CONTEXT = 'Changes to registered event';

// ── Email pre-built templates ───────────────────────────────────────────────
export const DEFAULT_EMAIL_TEMPLATES = [
  // ── Triggered Campaigns ──────────────────────────────────────────────────
  {
    name: 'Welcome Member',
    category: 'triggered',
    triggerContext: 'New member registration',
    audience: 'members',
    subject: 'Welcome to {{orgName}}!',
    body: 'Dear {{firstName}},\n\nWelcome to {{orgName}}! We are so glad you have joined our community.\n\nAs a member, you will have access to a wide range of support programmes, events, and resources designed to help you along your journey.\n\nHere is how to get started:\n• Complete your profile\n• Browse our upcoming events\n• Join a support group that suits you\n\nIf you have any questions or need assistance, please do not hesitate to reach out to us.\n\nWarm regards,\n{{orgName}} Team',
  },
  {
    name: 'Welcome Volunteer',
    category: 'triggered',
    triggerContext: 'New volunteer registration',
    audience: 'volunteers',
    subject: 'Welcome to the {{orgName}} Volunteer Team, {{firstName}}!',
    body: 'Dear {{firstName}},\n\nWelcome to the {{orgName}} volunteer team! We are truly grateful for your commitment and generosity in dedicating your time to support our community.\n\nAs a volunteer, you will have the opportunity to:\n• Sign up for upcoming shifts and events\n• Connect with fellow volunteers\n• Make a real difference in the lives of our members\n\nPlease log in to our platform to explore available opportunities and sign up for your first shift.\n\nWe look forward to working with you.\n\nWarm regards,\n{{orgName}} Team',
  },
  {
    name: 'Event Registration Confirmation',
    category: 'triggered',
    triggerContext: 'Member event registration',
    audience: 'members',
    subject: 'Registration Confirmed — {{eventName}}',
    body: 'Dear {{firstName}},\n\nYour registration for {{eventName}} has been confirmed. We look forward to seeing you there!\n\nEvent: {{eventName}}\nDate: {{eventDate}}\n\nPlease note this date in your calendar. If you are unable to attend, kindly let us know as soon as possible so we can open your spot to others.\n\nSee you soon!\n\nWarm regards,\n{{orgName}} Team',
  },
  {
    name: 'Event Volunteering Confirmation',
    category: 'triggered',
    triggerContext: 'Volunteer event sign-up',
    audience: 'volunteers',
    subject: 'Volunteer Shift Confirmed — {{eventName}}',
    body: 'Dear {{firstName}},\n\nThank you for signing up to volunteer at {{eventName}}! Your shift has been confirmed.\n\nEvent: {{eventName}}\nDate: {{eventDate}}\n\nPlease arrive on time and check in with the event coordinator upon arrival. If you are unable to make it, please inform us as soon as possible so we can arrange cover.\n\nWe truly appreciate your support.\n\nWarm regards,\n{{orgName}} Team',
  },
  // ── AdHoc Campaigns ──────────────────────────────────────────────────────
  {
    name: 'Monthly Event Blast',
    category: 'adhoc',
    triggerContext: 'Monthly event newsletter',
    audience: 'both',
    subject: '{{month}} Events at {{orgName}}',
    body: 'Dear {{firstName}},\n\nHere is a roundup of exciting events happening at {{orgName}} this {{month}}!\n\n[List your events for {{month}} here — include event names, dates, and brief descriptions]\n\nTo register for any of these events, log in to your account and navigate to the Events section.\n\nWe hope to see you at one (or more!) of these events.\n\nWarm regards,\n{{orgName}} Team',
  },
  {
    name: 'Call for Volunteers for Event',
    category: 'adhoc',
    triggerContext: 'Event volunteer recruitment',
    audience: 'volunteers',
    subject: 'We Need Volunteers — {{eventName}}',
    body: 'Dear {{firstName}},\n\nWe are looking for dedicated volunteers to help make {{eventName}} a success!\n\nIf you are available and interested in contributing your time, we would love to have you on board.\n\nTo sign up as a volunteer for {{eventName}}, please log in to your account and visit the Events section.\n\nEvery helping hand makes a difference. Thank you for considering this opportunity.\n\nWarm regards,\n{{orgName}} Team',
  },
  // Sent per registrant when an upcoming event's start/end date-time or location name/address
  // changes. Keep this LAST in the array: template ids are default_email_<index>.
  {
    name: 'Changes to Registered Event',
    category: 'triggered',
    triggerContext: EVENT_CHANGE_TRIGGER_CONTEXT,
    audience: 'members',
    subject: 'Update to {{eventName}} — please confirm your registration',
    body: 'Dear {{firstName}},\n\nThere has been a change to an event you registered for: {{eventName}}.\n\nWhat changed:\n{{changeSummary}}\n\nUpdated event details:\nDate & time: {{eventDate}}\nLocation: {{eventLocation}}\n\nPlease let us know what you would like to do with your registration:\n\n1) Keep my registration: {{keepLink}}\n2) Cancel my registration completely: {{cancelLink}}\n\nIf you do not respond, your registration stays as it is.\n\nWarm regards,\n{{orgName}} Team',
  },
];

// ── Push pre-built templates ────────────────────────────────────────────────
export const DEFAULT_PUSH_TEMPLATES = [
  // ── Triggered Campaigns ──────────────────────────────────────────────────
  {
    name: 'Welcome Member',
    category: 'triggered',
    triggerContext: 'New member registration',
    audience: 'members',
    title: 'Welcome to {{orgName}}! 🌸',
    body: 'We\'re so glad you\'re here. Tap to complete your profile and get started.',
  },
  {
    name: 'Welcome Volunteer',
    category: 'triggered',
    triggerContext: 'New volunteer registration',
    audience: 'volunteers',
    title: 'Welcome to the Team, {{firstName}}! 🙌',
    body: 'Thank you for joining our volunteer community. Tap to explore available opportunities.',
  },
  {
    name: 'Event Registration Confirmation',
    category: 'triggered',
    triggerContext: 'Member event registration',
    audience: 'members',
    title: 'You\'re Registered! 🎉',
    body: 'Your spot at {{eventName}} is confirmed. We look forward to seeing you!',
  },
  {
    name: 'Event Volunteering Confirmation',
    category: 'triggered',
    triggerContext: 'Volunteer event sign-up',
    audience: 'volunteers',
    title: 'Shift Confirmed! 📋',
    body: 'Your volunteer shift for {{eventName}} is confirmed. Thank you for stepping up!',
  },
  // ── AdHoc Campaigns ──────────────────────────────────────────────────────
  {
    name: 'Monthly Event Blast',
    category: 'adhoc',
    triggerContext: 'Monthly event newsletter',
    audience: 'both',
    title: '{{month}} Events at {{orgName}} 🗓️',
    body: 'Check out what\'s on this {{month}}. Tap to browse and register for events!',
  },
  {
    name: 'Call for Volunteers for Event',
    category: 'adhoc',
    triggerContext: 'Event volunteer recruitment',
    audience: 'volunteers',
    title: 'Volunteers Needed! 🙋',
    body: 'We\'re looking for volunteers for {{eventName}}. Tap to sign up and make a difference!',
  },
  // Keep LAST: ids are default_push_<index>. Tapping the notification opens the Keep / Cancel page.
  {
    name: 'Changes to Registered Event',
    category: 'triggered',
    triggerContext: EVENT_CHANGE_TRIGGER_CONTEXT,
    audience: 'members',
    title: 'Update to {{eventName}}',
    body: 'Details of {{eventName}} have changed. Tap to keep or cancel your registration.',
  },
];

// ── WhatsApp pre-built templates ────────────────────────────────────────────
// campaignType: 'triggered' | 'adhoc'  (for UI grouping)
// category: Meta Business API category (Marketing | Utility | Authentication)
export const DEFAULT_WHATSAPP_TEMPLATES = [
  // ── Triggered Campaigns ──────────────────────────────────────────────────
  {
    name: 'welcome_member',
    displayName: 'Welcome Member',
    campaignType: 'triggered',
    category: 'Marketing',
    triggerContext: 'New member registration',
    audience: 'members',
    approvalStatus: 'pending',
    body: 'Hi {{1}}, welcome to {{2}}! We\'re so glad you\'ve joined our community. We look forward to supporting you on your journey. 🌸',
  },
  {
    name: 'welcome_volunteer',
    displayName: 'Welcome Volunteer',
    campaignType: 'triggered',
    category: 'Marketing',
    triggerContext: 'New volunteer registration',
    audience: 'volunteers',
    approvalStatus: 'pending',
    body: 'Hi {{1}}, welcome to the {{2}} volunteer team! Thank you for dedicating your time to make a real difference. We\'re thrilled to have you with us. 🙌',
  },
  {
    name: 'event_registration_confirmation',
    displayName: 'Event Registration Confirmation',
    campaignType: 'triggered',
    category: 'Utility',
    triggerContext: 'Member event registration',
    audience: 'members',
    approvalStatus: 'pending',
    body: 'Hi {{1}}, your registration for {{2}} is confirmed! We look forward to seeing you there. 🎉',
  },
  {
    name: 'event_volunteering_confirmation',
    displayName: 'Event Volunteering Confirmation',
    campaignType: 'triggered',
    category: 'Utility',
    triggerContext: 'Volunteer event sign-up',
    audience: 'volunteers',
    approvalStatus: 'pending',
    body: 'Hi {{1}}, your volunteer shift for {{2}} is confirmed. Thank you for stepping up — your help means so much to us! 🙏',
  },
  // ── AdHoc Campaigns ──────────────────────────────────────────────────────
  {
    name: 'monthly_event_blast',
    displayName: 'Monthly Event Blast',
    campaignType: 'adhoc',
    category: 'Marketing',
    triggerContext: 'Monthly event newsletter',
    audience: 'both',
    approvalStatus: 'pending',
    body: 'Hi {{1}}, check out what\'s happening at {{2}} this {{3}}! Log in to browse and register for upcoming events. 🗓️',
  },
  {
    name: 'call_for_volunteers',
    displayName: 'Call for Volunteers for Event',
    campaignType: 'adhoc',
    category: 'Utility',
    triggerContext: 'Event volunteer recruitment',
    audience: 'volunteers',
    approvalStatus: 'pending',
    body: 'Hi {{1}}, we\'re looking for volunteers for {{2}}! Your support would make a real difference. Sign up today. 🙌',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// TEMPLATE SEEDING (call once per org on first module load)
// ═══════════════════════════════════════════════════════════════════════════

export const seedDefaultTemplates = async (orgId) => {
  try {
    const [emailSnap, pushSnap, waSnap] = await Promise.all([
      getDocs(orgCol(orgId, 'emailTemplates')),
      getDocs(orgCol(orgId, 'pushTemplates')),
      getDocs(orgCol(orgId, 'whatsappTemplates')),
    ]);

    const emailHasDefaults = emailSnap.docs.some(d => d.id.startsWith('default_email_'));
    const pushHasDefaults  = pushSnap.docs.some(d => d.id.startsWith('default_push_'));
    const waHasDefaults    = waSnap.docs.some(d => d.id.startsWith('default_wa_'));

    const batch = writeBatch(db);

    // Orgs seeded before the "Changes to Registered Event" campaign existed: add just that one.
    const ensureEventChange = (snap, templates, idPrefix, col, channel) => {
      const idx = templates.findIndex(t => t.triggerContext === EVENT_CHANGE_TRIGGER_CONTEXT);
      if (idx < 0 || snap.docs.some(d => d.id === `${idPrefix}${idx}`)) return false;
      batch.set(orgDoc(orgId, col, `${idPrefix}${idx}`), {
        ...templates[idx], channel, isDefault: true, createdAt: serverTimestamp(),
      });
      return true;
    };
    let addedEventChange = false;
    if (emailHasDefaults) addedEventChange = ensureEventChange(emailSnap, DEFAULT_EMAIL_TEMPLATES, 'default_email_', 'emailTemplates', 'email') || addedEventChange;
    if (pushHasDefaults)  addedEventChange = ensureEventChange(pushSnap,  DEFAULT_PUSH_TEMPLATES,  'default_push_',  'pushTemplates',  'push')  || addedEventChange;

    if (emailHasDefaults && pushHasDefaults && waHasDefaults) {
      if (addedEventChange) await batch.commit();
      return;
    }

    if (!emailHasDefaults) {
      DEFAULT_EMAIL_TEMPLATES.forEach((tpl, i) => {
        batch.set(orgDoc(orgId, 'emailTemplates', `default_email_${i}`), {
          ...tpl, channel: 'email', isDefault: true, createdAt: serverTimestamp(),
        });
      });
    }
    if (!pushHasDefaults) {
      DEFAULT_PUSH_TEMPLATES.forEach((tpl, i) => {
        batch.set(orgDoc(orgId, 'pushTemplates', `default_push_${i}`), {
          ...tpl, channel: 'push', isDefault: true, createdAt: serverTimestamp(),
        });
      });
    }
    if (!waHasDefaults) {
      DEFAULT_WHATSAPP_TEMPLATES.forEach((tpl, i) => {
        batch.set(orgDoc(orgId, 'whatsappTemplates', `default_wa_${i}`), {
          ...tpl, channel: 'whatsapp', isDefault: true, createdAt: serverTimestamp(),
        });
      });
    }

    await batch.commit();
  } catch (err) {
    console.error('seedDefaultTemplates error:', err);
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// AUDIENCE RESOLUTION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Audience types: 'members' | 'volunteers' | 'donors'
 *   'both' = members + volunteers (legacy, kept for campaigns saved before donors were targetable)
 *   'all'  = members + volunteers + donors
 */
const includesAudience = (audienceType, kind) => {
  if (kind === 'members')    return ['members', 'both', 'all'].includes(audienceType);
  if (kind === 'volunteers') return ['volunteers', 'both', 'all'].includes(audienceType);
  if (kind === 'donors')     return ['donors', 'all'].includes(audienceType);
  return false;
};

/**
 * Donors can only be emailed. They never log into a portal, so no push token or
 * subscription/WhatsApp opt-in is ever captured for them; those fields are ignored.
 */
const DONOR_EMAIL_ONLY_FIELDS = ['fcmToken', 'fcmTokens', 'subscriptionPreferences', 'whatsappOptIn', 'whatsappPhone'];

/** donorContacts docs use name/email/phone; map to the shape the campaign screens expect. */
const donorContactFromDoc = (d) => {
  const data = d.data();
  DONOR_EMAIL_ONLY_FIELDS.forEach(f => delete data[f]);
  return {
    id: d.id, _type: 'donor', _collection: 'donorContacts', ...data,
    displayName: data.displayName || data.name || '',
  };
};

/**
 * Resolve audience list based on audienceType + audienceFilters.
 * Returns { contacts: [...], totalCount, eligibleCount }
 * 'eligible' = has email (for email); has fcmToken (for push); whatsappOptIn===true (for WA)
 */
export const resolveAudience = async (orgId, audienceType, audienceFilters = {}, channel = 'email') => {
  let contacts = [];

  if (includesAudience(audienceType, 'members')) {
    const snap = await getDocs(query(collection(db, 'members'), where('orgId', '==', orgId)));
    const members = snap.docs.map(d => ({ id: d.id, _type: 'member', ...d.data() }));
    contacts.push(...members);
  }

  if (includesAudience(audienceType, 'volunteers')) {
    const snap = await getDocs(query(collection(db, 'volunteers'), where('orgId', '==', orgId)));
    const vols = snap.docs.map(d => ({ id: d.id, _type: 'volunteer', ...d.data() }));
    contacts.push(...vols);
  }

  if (channel === 'email' && includesAudience(audienceType, 'donors')) {
    const snap = await getDocs(query(collection(db, 'donorContacts'), where('orgId', '==', orgId)));
    contacts.push(...snap.docs.map(donorContactFromDoc));
  }

  // Apply audience filters
  let filtered = contacts;

  if (audienceFilters.specificContactIds !== undefined) {
    // Explicit specific-contact list: return only those IDs (empty list = no recipients)
    filtered = contacts.filter(c => (audienceFilters.specificContactIds || []).includes(c.id));
  } else {
    if (audienceFilters.memberStatus?.length > 0) {
      filtered = filtered.filter(c =>
        c._type !== 'member' || audienceFilters.memberStatus.includes(c.status)
      );
    }

    if (audienceFilters.memberDistrict) {
      filtered = filtered.filter(c =>
        c._type !== 'member' || c.district === audienceFilters.memberDistrict
      );
    }

    if (audienceFilters.birthMonths?.length > 0) {
      filtered = filtered.filter(c =>
        c._type === 'member' && audienceFilters.birthMonths.includes(c.memberBirthMonth)
      );
    }

    if (audienceFilters.volunteerStatus?.length > 0) {
      filtered = filtered.filter(c =>
        c._type !== 'volunteer' || audienceFilters.volunteerStatus.includes(c.status)
      );
    }

    if (audienceFilters.volunteerDistrict) {
      filtered = filtered.filter(c =>
        c._type !== 'volunteer' || c.district === audienceFilters.volunteerDistrict
      );
    }
  }

  const totalCount = filtered.length;

  // Determine eligible count by channel
  let eligible;
  if (channel === 'push') {
    eligible = filtered.filter(c => c.fcmToken);
  } else if (channel === 'whatsapp') {
    eligible = filtered.filter(c => c.whatsappOptIn === true && c.whatsappPhone);
  } else {
    // email
    eligible = filtered.filter(c => c.email);
  }

  return {
    contacts: filtered,
    eligibleContacts: eligible,
    totalCount,
    eligibleCount: eligible.length,
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// EMAIL TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

const dedupeByName = (templates) => {
  const seen = new Map();
  for (const t of templates) {
    const key = t.name || t.id;
    if (!seen.has(key) || (!seen.get(key).channel && t.channel)) {
      seen.set(key, t);
    }
  }
  return [...seen.values()];
};

export const getEmailTemplates = async (orgId) => {
  try {
    const snap = await getDocs(query(orgCol(orgId, 'emailTemplates'), orderBy('createdAt', 'asc')));
    return dedupeByName(toArr(snap).filter(t => !t.channel || t.channel === 'email'));
  } catch {
    const snap = await getDocs(orgCol(orgId, 'emailTemplates'));
    return dedupeByName(toArr(snap).filter(t => !t.channel || t.channel === 'email'));
  }
};

export const createEmailTemplate = async (orgId, data, adminId) => {
  const ref = await addDoc(orgCol(orgId, 'emailTemplates'), {
    ...data, channel: 'email', isDefault: false, createdBy: adminId, createdAt: now(), updatedAt: now()
  });
  return ref.id;
};

export const updateEmailTemplate = async (orgId, templateId, data) => {
  await updateDoc(orgDoc(orgId, 'emailTemplates', templateId), { ...data, updatedAt: now() });
};

export const deleteEmailTemplate = async (orgId, templateId) => {
  await deleteDoc(orgDoc(orgId, 'emailTemplates', templateId));
};

// ═══════════════════════════════════════════════════════════════════════════
// EMAIL CAMPAIGNS
// ═══════════════════════════════════════════════════════════════════════════

export const getEmailCampaigns = async (orgId, statusFilter = null) => {
  let q = query(orgCol(orgId, 'emailCampaigns'), orderBy('createdAt', 'desc'));
  if (statusFilter) {
    q = query(orgCol(orgId, 'emailCampaigns'), where('status', '==', statusFilter), orderBy('createdAt', 'desc'));
  }
  return toArr(await getDocs(q));
};

export const getEmailCampaign = async (orgId, campaignId) => {
  const snap = await getDoc(orgDoc(orgId, 'emailCampaigns', campaignId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const createEmailCampaign = async (orgId, data, adminId) => {
  const ref = await addDoc(orgCol(orgId, 'emailCampaigns'), {
    name: data.name || 'Untitled Campaign',
    status: 'draft',
    templateId: data.templateId || null,
    subject: data.subject || '',
    body: data.body || '',
    audienceType: data.audienceType || 'members',
    audienceFilters: data.audienceFilters || {},
    recipientCount: 0,
    scheduledAt: null,
    sentAt: null,
    createdBy: adminId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

export const updateEmailCampaign = async (orgId, campaignId, data) => {
  await updateDoc(orgDoc(orgId, 'emailCampaigns', campaignId), { ...data, updatedAt: now() });
};

export const scheduleEmailCampaign = async (orgId, campaignId, scheduledAt, recipientCount, adminId) => {
  await updateDoc(orgDoc(orgId, 'emailCampaigns', campaignId), {
    status: 'scheduled',
    scheduledAt: Timestamp.fromDate(new Date(scheduledAt)),
    recipientCount,
    updatedAt: now(),
  });
};

export const sendEmailCampaignNow = async (orgId, campaignId, recipientCount, adminId) => {
  await updateDoc(orgDoc(orgId, 'emailCampaigns', campaignId), {
    status: 'sent',
    sentAt: now(),
    scheduledAt: now(),
    recipientCount,
    updatedAt: now(),
  });
};

const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Campaign body (plain text or editor HTML, with optional [IMAGE:url] tokens) → email HTML. */
const emailBodyToHtml = (body, orgName) => {
  const isHtml = /<[a-z]/i.test(body);
  const content = (isHtml ? body : escapeHtml(body).replace(/\n/g, '<br>'))
    .replace(/\[IMAGE:(.*?)\]/g, (_, url) => /^https?:\/\//i.test(url.trim())
      ? `<img src="${url.trim()}" style="max-width:100%;height:auto;display:block;margin:6px 0;" alt="" />`
      : '');
  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.6;color:#1f2937;max-width:600px;margin:0 auto;padding:16px;">`
    + content
    + `<hr style="border:none;border-top:1px solid #e5e7eb;margin:24px 0 12px;">`
    + `<p style="font-size:12px;color:#6b7280;margin:0;">You are receiving this email from ${escapeHtml(orgName || 'BespokeBridge')}. `
    + `You can change your email preferences in your profile.</p></div>`;
};

/**
 * Queue an email campaign for delivery. Writes one document per recipient to the top-level
 * `mail` collection; the `sendEmail` Cloud Function (functions/index.js) picks each one up,
 * sends it through SendGrid and records the outcome in `delivery` on the same document.
 * `contacts` must already be the final list (unsubscribes / frequency limits applied).
 * Returns { queued, failedToQueue }.
 */
export const dispatchEmailCampaign = async (orgId, campaignId, { subject, body, orgName, contacts, customMergeValues }) => {
  const mailCol = collection(db, 'mail');
  const recipients = contacts.filter(c => c.email);
  let queued = 0;

  for (let i = 0; i < recipients.length; i += 25) {
    const results = await Promise.allSettled(recipients.slice(i, i + 25).map(c => {
      const displayName = c.displayName || c.name || c.fullName
        || [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Friend';
      const personal = { ...c, displayName };
      const html = emailBodyToHtml(resolveMergeFields(body, personal, orgName, customMergeValues), orgName);
      return addDoc(mailCol, {
        to: [c.email.trim()],
        message: {
          subject: resolveMergeFields(subject, personal, orgName, customMergeValues),
          html,
          text: html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, ''),
        },
        metadata: {
          orgId,
          campaignId,
          campaignType: 'email',
          contactId: c.id,
          contactType: c._type || 'unknown',
          queuedAt: new Date().toISOString(),
        },
      });
    }));
    results.forEach(r => {
      if (r.status === 'fulfilled') queued++;
      else console.error('dispatchEmailCampaign: could not queue email:', r.reason);
    });
  }

  return { queued, failedToQueue: recipients.length - queued };
};

export const cancelEmailCampaign = async (orgId, campaignId) => {
  await updateDoc(orgDoc(orgId, 'emailCampaigns', campaignId), {
    status: 'cancelled', updatedAt: now()
  });
};

export const duplicateEmailCampaign = async (orgId, campaignId, adminId) => {
  const original = await getEmailCampaign(orgId, campaignId);
  if (!original) throw new Error('Campaign not found');
  const { id, createdAt, updatedAt, sentAt, scheduledAt, ...rest } = original;
  const ref = await addDoc(orgCol(orgId, 'emailCampaigns'), {
    ...rest,
    name: `${rest.name} (Copy)`,
    status: 'draft',
    recipientCount: 0,
    scheduledAt: null,
    sentAt: null,
    createdBy: adminId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

// ═══════════════════════════════════════════════════════════════════════════
// PUSH NOTIFICATION TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

export const getPushTemplates = async (orgId) => {
  try {
    const snap = await getDocs(query(orgCol(orgId, 'pushTemplates'), orderBy('createdAt', 'asc')));
    return dedupeByName(toArr(snap).filter(t => !t.channel || t.channel === 'push'));
  } catch {
    const snap = await getDocs(orgCol(orgId, 'pushTemplates'));
    return dedupeByName(toArr(snap).filter(t => !t.channel || t.channel === 'push'));
  }
};

export const createPushTemplate = async (orgId, data, adminId) => {
  const ref = await addDoc(orgCol(orgId, 'pushTemplates'), {
    ...data, channel: 'push', isDefault: false, createdBy: adminId, createdAt: now(), updatedAt: now()
  });
  return ref.id;
};

export const updatePushTemplate = async (orgId, templateId, data) => {
  await updateDoc(orgDoc(orgId, 'pushTemplates', templateId), { ...data, updatedAt: now() });
};

// ═══════════════════════════════════════════════════════════════════════════
// PUSH CAMPAIGNS
// ═══════════════════════════════════════════════════════════════════════════

export const getPushCampaigns = async (orgId, statusFilter = null) => {
  let q = query(orgCol(orgId, 'pushCampaigns'), orderBy('createdAt', 'desc'));
  if (statusFilter) {
    q = query(orgCol(orgId, 'pushCampaigns'), where('status', '==', statusFilter), orderBy('createdAt', 'desc'));
  }
  return toArr(await getDocs(q));
};

export const getPushCampaign = async (orgId, campaignId) => {
  const snap = await getDoc(orgDoc(orgId, 'pushCampaigns', campaignId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const createPushCampaign = async (orgId, data, adminId) => {
  const ref = await addDoc(orgCol(orgId, 'pushCampaigns'), {
    name: data.name || 'Untitled Campaign',
    status: 'draft',
    templateId: data.templateId || null,
    title: data.title || '',
    body: data.body || '',
    deepLinkUrl: data.deepLinkUrl || null,
    imageUrl: data.imageUrl || null,
    audienceType: data.audienceType || 'members',
    audienceFilters: data.audienceFilters || {},
    recipientCount: 0,
    scheduledAt: null,
    sentAt: null,
    createdBy: adminId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

export const updatePushCampaign = async (orgId, campaignId, data) => {
  await updateDoc(orgDoc(orgId, 'pushCampaigns', campaignId), { ...data, updatedAt: now() });
};

export const schedulePushCampaign = async (orgId, campaignId, scheduledAt, recipientCount) => {
  await updateDoc(orgDoc(orgId, 'pushCampaigns', campaignId), {
    status: 'scheduled',
    scheduledAt: Timestamp.fromDate(new Date(scheduledAt)),
    recipientCount,
    updatedAt: now(),
  });
};

/**
 * Deliver a push campaign now. The backend sends it through Firebase Cloud Messaging and records the
 * outcome (and a reason for every recipient that did not get it) on the campaign document.
 * `excludeContactIds` are people the screen already removed (frequency limits).
 * Returns { status, failureReason, counts: { sent, failed, no_device, unsubscribed } }.
 */
export const sendPushCampaignNow = async (orgId, campaignId, excludeContactIds = []) => {
  if (!auth.currentUser) throw new Error('Please sign in again.');
  const idToken = await auth.currentUser.getIdToken();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  let res;
  try {
    res = await fetch(`${API_URL}/api/org/${encodeURIComponent(orgId)}/push-campaigns/${encodeURIComponent(campaignId)}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ excludeIds: excludeContactIds }),
    });
  } catch {
    throw new Error('Could not reach the server. Please check your connection and try again.');
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) throw new Error(body.message || `Send failed (${res.status})`);
  return body;
};

export const cancelPushCampaign = async (orgId, campaignId) => {
  await updateDoc(orgDoc(orgId, 'pushCampaigns', campaignId), {
    status: 'cancelled', updatedAt: now()
  });
};

export const duplicatePushCampaign = async (orgId, campaignId, adminId) => {
  const original = await getPushCampaign(orgId, campaignId);
  if (!original) throw new Error('Campaign not found');
  const { id, createdAt, updatedAt, sentAt, scheduledAt, ...rest } = original;
  const ref = await addDoc(orgCol(orgId, 'pushCampaigns'), {
    ...rest,
    name: `${rest.name} (Copy)`,
    status: 'draft',
    recipientCount: 0,
    scheduledAt: null,
    sentAt: null,
    createdBy: adminId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

// ═══════════════════════════════════════════════════════════════════════════
// WHATSAPP TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

export const getWhatsAppTemplates = async (orgId) => {
  try {
    const snap = await getDocs(query(orgCol(orgId, 'whatsappTemplates'), orderBy('createdAt', 'asc')));
    return dedupeByName(toArr(snap).filter(t => !t.channel || t.channel === 'whatsapp'));
  } catch {
    const snap = await getDocs(orgCol(orgId, 'whatsappTemplates'));
    return dedupeByName(toArr(snap).filter(t => !t.channel || t.channel === 'whatsapp'));
  }
};

export const createWhatsAppTemplate = async (orgId, data, adminId) => {
  const ref = await addDoc(orgCol(orgId, 'whatsappTemplates'), {
    ...data,
    channel: 'whatsapp',
    approvalStatus: 'pending',
    isDefault: false,
    createdBy: adminId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

export const updateWhatsAppTemplate = async (orgId, templateId, data) => {
  await updateDoc(orgDoc(orgId, 'whatsappTemplates', templateId), { ...data, updatedAt: now() });
};

export const deleteWhatsAppTemplate = async (orgId, templateId) => {
  await deleteDoc(orgDoc(orgId, 'whatsappTemplates', templateId));
};

// ═══════════════════════════════════════════════════════════════════════════
// WHATSAPP CAMPAIGNS
// ═══════════════════════════════════════════════════════════════════════════

export const getWhatsAppCampaigns = async (orgId, statusFilter = null) => {
  let q = query(orgCol(orgId, 'whatsappCampaigns'), orderBy('createdAt', 'desc'));
  if (statusFilter) {
    q = query(orgCol(orgId, 'whatsappCampaigns'), where('status', '==', statusFilter), orderBy('createdAt', 'desc'));
  }
  return toArr(await getDocs(q));
};

export const getWhatsAppCampaign = async (orgId, campaignId) => {
  const snap = await getDoc(orgDoc(orgId, 'whatsappCampaigns', campaignId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const createWhatsAppCampaign = async (orgId, data, adminId) => {
  const ref = await addDoc(orgCol(orgId, 'whatsappCampaigns'), {
    name: data.name || 'Untitled Campaign',
    status: 'draft',
    templateId: data.templateId || null,
    templateName: data.templateName || '',
    templateVariables: data.templateVariables || {},
    audienceType: data.audienceType || 'members',
    audienceFilters: data.audienceFilters || {},
    eligibleRecipientCount: 0,
    totalAudienceCount: 0,
    scheduledAt: null,
    sentAt: null,
    createdBy: adminId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

export const updateWhatsAppCampaign = async (orgId, campaignId, data) => {
  await updateDoc(orgDoc(orgId, 'whatsappCampaigns', campaignId), { ...data, updatedAt: now() });
};

export const scheduleWhatsAppCampaign = async (orgId, campaignId, scheduledAt, eligibleCount, totalCount) => {
  await updateDoc(orgDoc(orgId, 'whatsappCampaigns', campaignId), {
    status: 'scheduled',
    scheduledAt: Timestamp.fromDate(new Date(scheduledAt)),
    eligibleRecipientCount: eligibleCount,
    totalAudienceCount: totalCount,
    updatedAt: now(),
  });
};

export const sendWhatsAppCampaignNow = async (orgId, campaignId, eligibleCount, totalCount) => {
  await updateDoc(orgDoc(orgId, 'whatsappCampaigns', campaignId), {
    status: 'sent',
    sentAt: now(),
    scheduledAt: now(),
    eligibleRecipientCount: eligibleCount,
    totalAudienceCount: totalCount,
    updatedAt: now(),
  });
};

export const cancelWhatsAppCampaign = async (orgId, campaignId) => {
  await updateDoc(orgDoc(orgId, 'whatsappCampaigns', campaignId), {
    status: 'cancelled', updatedAt: now()
  });
};

export const duplicateWhatsAppCampaign = async (orgId, campaignId, adminId) => {
  const original = await getWhatsAppCampaign(orgId, campaignId);
  if (!original) throw new Error('Campaign not found');
  const { id, createdAt, updatedAt, sentAt, scheduledAt, ...rest } = original;
  const ref = await addDoc(orgCol(orgId, 'whatsappCampaigns'), {
    ...rest,
    name: `${rest.name} (Copy)`,
    status: 'draft',
    eligibleRecipientCount: 0,
    totalAudienceCount: 0,
    scheduledAt: null,
    sentAt: null,
    createdBy: adminId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

// ═══════════════════════════════════════════════════════════════════════════
// WHATSAPP OPT-IN MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get all contacts (members + volunteers) with their WhatsApp opt-in status.
 * audienceType: 'members' | 'volunteers' | 'both'
 */
export const getWhatsAppOptInList = async (orgId, audienceType = 'both') => {
  const contacts = [];

  if (audienceType === 'members' || audienceType === 'both') {
    const snap = await getDocs(query(collection(db, 'members'), where('orgId', '==', orgId)));
    snap.docs.forEach(d => contacts.push({ id: d.id, _type: 'member', _collection: 'members', ...d.data() }));
  }
  if (audienceType === 'volunteers' || audienceType === 'both') {
    const snap = await getDocs(query(collection(db, 'volunteers'), where('orgId', '==', orgId)));
    snap.docs.forEach(d => contacts.push({ id: d.id, _type: 'volunteer', _collection: 'volunteers', ...d.data() }));
  }
  return contacts;
};

/**
 * Manually update a contact's WhatsApp opt-in status (admin override).
 * Logs reason + adminId as required by spec compliance.
 */
export const updateWhatsAppOptIn = async (orgId, contactId, contactCollection, optIn, adminId, reason = '') => {
  const ref = doc(db, 'organizations', orgId, contactCollection, contactId);
  const update = {
    whatsappOptIn: optIn,
    whatsappOptInSource: 'admin_manual',
    updatedAt: now(),
  };
  if (optIn) {
    update.whatsappOptInAt = now();
    update.whatsappOptOutAt = null;
  } else {
    update.whatsappOptOutAt = now();
  }
  // Audit log entry stored on the contact doc
  update._whatsappOptInAudit = {
    changedBy: adminId,
    changedAt: new Date().toISOString(),
    newValue: optIn,
    reason: reason || (optIn ? 'Admin manual opt-in' : 'Admin manual opt-out'),
  };
  await updateDoc(ref, update);
};

/**
 * Generate CSV of opt-in list for compliance export.
 */
export const exportOptInCSV = (contacts) => {
  const rows = [['Name', 'Type', 'Email', 'WhatsApp Phone', 'Opted In', 'Opt-In Date', 'Opt-In Source']];
  contacts.forEach(c => {
    rows.push([
      `"${c.displayName || c.name || ''}"`,
      c._type,
      `"${c.email || ''}"`,
      `"${c.whatsappPhone || ''}"`,
      c.whatsappOptIn ? 'Yes' : 'No',
      c.whatsappOptInAt ? (c.whatsappOptInAt.toDate ? c.whatsappOptInAt.toDate().toLocaleDateString('en-SG') : new Date(c.whatsappOptInAt).toLocaleDateString('en-SG')) : '—',
      c.whatsappOptInSource || '—',
    ]);
  });
  return rows.map(r => r.join(',')).join('\n');
};

// ═══════════════════════════════════════════════════════════════════════════
// GENERIC CHANNEL OPT-IN MANAGEMENT (Email / Push / WhatsApp)
// ═══════════════════════════════════════════════════════════════════════════

// Maps internal channel keys → Firestore field names
const CHANNEL_MAP = {
  email:    { prefField: 'email',             auditField: '_emailOptInAudit' },
  push:     { prefField: 'pushNotifications', auditField: '_pushnotificationOptInAudit' },
  whatsapp: { prefField: 'whatsApp',          auditField: '_whatsappOptInAudit' },
};

/**
 * Resolve a contact's effective opt-in status for a channel.
 * Reads from subscriptionPreferences using the correct Firestore field name.
 * Falls back to legacy whatsappOptIn for whatsapp; defaults true for email/push.
 */
export const getContactOptIn = (contact, channel) => {
  const fieldName = CHANNEL_MAP[channel]?.prefField || channel;
  const sp = contact.subscriptionPreferences;
  if (sp && fieldName in sp) return !!sp[fieldName];
  if (channel === 'whatsapp') return contact.whatsappOptIn !== false;
  return true;
};

/**
 * Fetch all contacts (members + volunteers) for opt-in management.
 */
export const getChannelOptInList = async (orgId, audienceType = 'both') => {
  const contacts = [];
  if (audienceType === 'members' || audienceType === 'both') {
    const snap = await getDocs(query(collection(db, 'members'), where('orgId', '==', orgId)));
    snap.docs.forEach(d => contacts.push({ id: d.id, _type: 'member', _collection: 'members', ...d.data() }));
  }
  if (audienceType === 'volunteers' || audienceType === 'both') {
    const snap = await getDocs(query(collection(db, 'volunteers'), where('orgId', '==', orgId)));
    snap.docs.forEach(d => contacts.push({ id: d.id, _type: 'volunteer', _collection: 'volunteers', ...d.data() }));
  }
  return contacts;
};

/**
 * Admin override of a contact's channel opt-in. Appends to audit array so all
 * historical changes are preserved.
 * channel: 'email' | 'push' | 'whatsapp'
 */
export const updateChannelOptIn = async (orgId, contactId, contactCollection, channel, optIn, adminId, reason = '') => {
  const { prefField, auditField } = CHANNEL_MAP[channel] || { prefField: channel, auditField: `_${channel}OptInAudit` };
  const ref = doc(db, contactCollection, contactId);
  const auditEntry = {
    changedBy: adminId,
    changedAt: new Date().toISOString(),
    newValue: optIn,
    reason: reason || (optIn ? 'Admin manual opt-in' : 'Admin manual opt-out'),
  };
  await updateDoc(ref, {
    [`subscriptionPreferences.${prefField}`]: optIn,
    updatedAt: now(),
    [auditField]: arrayUnion(auditEntry),
  });
};

/**
 * Export channel opt-in list as CSV.
 */
export const exportChannelOptInCSV = (contacts, channel) => {
  const label = { email: 'Email', push: 'Push Notifications', whatsapp: 'WhatsApp' }[channel] || channel;
  const auditField = CHANNEL_MAP[channel]?.auditField || `_${channel}OptInAudit`;
  const rows = [['Name', 'Type', 'Email', label + ' Opt-In', 'Opt-In Source']];
  contacts.forEach(c => {
    const optIn = getContactOptIn(c, channel);
    const audit = c[auditField];
    const source = audit ? 'admin_manual' : (c.subscriptionPreferences ? 'self_set' : 'default');
    rows.push([
      `"${c.displayName || c.name || ''}"`,
      c._type,
      `"${c.email || ''}"`,
      optIn ? 'Yes' : 'No',
      source,
    ]);
  });
  return rows.map(r => r.join(',')).join('\n');
};

/**
 * User-facing: save a contact's own subscription preferences.
 * Input uses internal keys { email, push, whatsapp, directMail };
 * stored using the correct Firestore field names.
 */
export const updateSubscriptionPreferences = async (contactCollection, contactId, prefs) => {
  const ref = doc(db, contactCollection, contactId);
  await updateDoc(ref, {
    subscriptionPreferences: {
      email:             !!prefs.email,
      pushNotifications: !!prefs.push,
      whatsApp:          !!prefs.whatsapp,
      directMail:        !!prefs.directMail,
    },
    updatedAt: now(),
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// CAMPAIGN LISTING (unified view across all 3 channels)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fetch all campaigns across email, push, and WhatsApp collections.
 * Returns a unified array sorted by createdAt desc, each item has _channel field.
 */
export const getAllCampaigns = async (orgId) => {
  const [emailSnap, pushSnap, waSnap] = await Promise.all([
    getDocs(query(orgCol(orgId, 'emailCampaigns'), orderBy('createdAt', 'desc'))),
    getDocs(query(orgCol(orgId, 'pushCampaigns'),   orderBy('createdAt', 'desc'))),
    getDocs(query(orgCol(orgId, 'whatsappCampaigns'), orderBy('createdAt', 'desc'))),
  ]);

  const emails = emailSnap.docs.map(d => ({
    id: d.id, _channel: 'email', _channelLabel: 'Email', ...d.data()
  }));
  const pushes = pushSnap.docs.map(d => ({
    id: d.id, _channel: 'push', _channelLabel: 'Push Notification', ...d.data()
  }));
  const was = waSnap.docs.map(d => ({
    id: d.id, _channel: 'whatsapp', _channelLabel: 'WhatsApp', ...d.data()
  }));

  const all = [...emails, ...pushes, ...was];

  // Sort by createdAt descending
  all.sort((a, b) => {
    const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
    const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
    return bDate - aDate;
  });

  return all;
};

/**
 * Apply client-side filters to a campaign list.
 * filters: { channel, status, audienceType, dateFrom, dateTo, search }
 */
export const applyCampaignFilters = (campaigns, filters = {}) => {
  let result = campaigns;

  if (filters.channel) {
    result = result.filter(c => c._channel === filters.channel);
  }
  if (filters.status) {
    result = result.filter(c => c.status === filters.status);
  }
  if (filters.audienceType) {
    result = result.filter(c => c.audienceType === filters.audienceType);
  }
  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(c => (c.name || '').toLowerCase().includes(q));
  }
  if (filters.dateFrom) {
    const from = new Date(filters.dateFrom);
    result = result.filter(c => {
      const d = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt || 0);
      return d >= from;
    });
  }
  if (filters.dateTo) {
    const to = new Date(filters.dateTo);
    to.setHours(23, 59, 59);
    result = result.filter(c => {
      const d = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt || 0);
      return d <= to;
    });
  }

  return result;
};

/**
 * Get the display recipient count for a campaign (handles email vs WA count fields).
 */
export const getRecipientDisplay = (campaign) => {
  if (campaign._channel === 'whatsapp') {
    const eligible = campaign.eligibleRecipientCount || 0;
    const total = campaign.totalAudienceCount || 0;
    return total > 0 ? `${eligible} / ${total}` : '—';
  }
  return campaign.recipientCount || '—';
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS FOR UI
// ═══════════════════════════════════════════════════════════════════════════

/** Resolve merge fields in a template body string using a contact object and org name.
 *  contact.displayName  → {{firstName}} (first word) and {{fullName}}
 *  orgName              → {{orgName}}  — must be fetched from organizations/{orgId}.name
 *  customValues         → admin-supplied values for static fields (eventName, eventDate, etc.)
 */
export const resolveMergeFields = (text, contact = {}, orgName = '', customValues = {}) => {
  const cv = customValues || {};
  return (text || '')
    .replace(/\{\{firstName\}\}/g, contact.displayName?.split(' ')[0] || '[First Name]')
    .replace(/\{\{fullName\}\}/g, contact.displayName || '[Full Name]')
    .replace(/\{\{orgName\}\}/g, orgName || '[Org Name]')
    .replace(/\{\{eventName\}\}/g, cv.eventName || '[Event Name]')
    .replace(/\{\{eventDate\}\}/g, cv.eventDate || '[Event Date]')
    .replace(/\{\{eventLocation\}\}/g, cv.eventLocation || '[Event Location]')
    .replace(/\{\{changeSummary\}\}/g, cv.changeSummary || '• Start date & time: [old] → [new]')
    .replace(/\{\{keepLink\}\}/g, cv.keepLink || '[Keep registration link]')
    .replace(/\{\{cancelLink\}\}/g, cv.cancelLink || '[Cancel registration link]')
    .replace(/\{\{manageLink\}\}/g, cv.manageLink || '[Manage registration link]')
    .replace(/\{\{month\}\}/g, cv.month || '[Month]')
    .replace(/\{\{programName\}\}/g, cv.programName || '[Programme Name]')
    .replace(/\{\{shiftDate\}\}/g, cv.shiftDate || '[Shift Date]')
    .replace(/\{\{hoursLogged\}\}/g, cv.hoursLogged || '[Hours]')
    .replace(/\{\{milestoneLabel\}\}/g, cv.milestoneLabel || '[Milestone]');
};

/** Fetch the organisation's display name from organizations/{orgId}.branding.name */
export const getOrgName = async (orgId) => {
  try {
    const snap = await getDoc(doc(db, 'organizations', orgId));
    if (!snap.exists()) return '';
    const data = snap.data();
    return data.branding?.name || data.profile?.name || '';
  } catch (err) {
    console.error('getOrgName:', err);
    return '';
  }
};

/** Fetch the display name of a staff member by their uid from the staff collection */
export const getStaffDisplayName = async (orgId, uid) => {
  try {
    const snap = await getDocs(query(
      collection(db, 'staff'),
      where('orgId', '==', orgId),
      where('uid', '==', uid)
    ));
    if (!snap.empty) return snap.docs[0].data().displayName || snap.docs[0].data().name || '';
    return '';
  } catch (err) {
    console.error('getStaffDisplayName:', err);
    return '';
  }
};

/** Push notification character limits per platform. */
export const PUSH_CHAR_LIMITS = { title: 65, bodyIOS: 178, bodyAndroid: 240 };

/** Get the shorter of the two body limits (iOS is more restrictive). */
export const getPushBodyLimit = () => PUSH_CHAR_LIMITS.bodyIOS;

// ═══════════════════════════════════════════════════════════════════════════
// CONTACT SEARCH (for specific-contact targeting)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Search members and volunteers by display name or email.
 * Returns up to 30 results sorted by display name.
 */
export const searchContacts = async (orgId, queryStr, audienceType = 'both') => {
  const q = (queryStr || '').toLowerCase().trim();
  const results = [];

  if (audienceType === 'members' || audienceType === 'both') {
    const snap = await getDocs(query(collection(db, 'members'), where('orgId', '==', orgId)));
    snap.docs.forEach(d => {
      const data = d.data();
      const name  = (data.displayName || data.name || '').toLowerCase();
      const email = (data.email || '').toLowerCase();
      if (!q || name.includes(q) || email.includes(q)) {
        results.push({ id: d.id, _type: 'member', displayName: data.displayName || data.name || '', email: data.email || '', whatsappPhone: data.whatsappPhone || '' });
      }
    });
  }

  if (audienceType === 'volunteers' || audienceType === 'both') {
    const snap = await getDocs(query(collection(db, 'volunteers'), where('orgId', '==', orgId)));
    snap.docs.forEach(d => {
      const data = d.data();
      const name  = (data.displayName || data.name || '').toLowerCase();
      const email = (data.email || '').toLowerCase();
      if (!q || name.includes(q) || email.includes(q)) {
        results.push({ id: d.id, _type: 'volunteer', displayName: data.displayName || data.name || '', email: data.email || '', whatsappPhone: data.whatsappPhone || '' });
      }
    });
  }

  results.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return results.slice(0, 30);
};
